import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { ConnectStatementsInstitution } from './useConnectStatementsData';
import { resolveConnectedInstitutionId } from './useConnectedInstitutionResolver';

import { createUseBankStatementsAvailabilityListKey } from 'services/bookkeeping/queries/useBankStatementsAvailabilityList';
import fetchBankStatementsAvailabilityList from 'services/bookkeeping/queries/useBankStatementsAvailabilityList/useBankStatementsAvailabilityList.request';

export const UNCONFIRMED_POLL_INTERVAL_MS = 5000;
// A connection outside the business accounts never lists, so polling for one has to stop.
export const UNCONFIRMED_POLL_WINDOW_MS = 2 * 60 * 1000;

interface UnconfirmedConnection {
    connectionId: string;
    /** The bank the connector was opened for — a guess, since the member may have connected another. */
    institutionId: string;
    since: number;
}

const isPolling = (connection: UnconfirmedConnection) => Date.now() - connection.since < UNCONFIRMED_POLL_WINDOW_MS;

/**
 * Connections Quiltt reported that the availability list did not show yet. Re-reads the list in the
 * background and calls onConfirmed with the bank each one turns out to cover.
 */
export default function useUnconfirmedConnections(
    institutions: ConnectStatementsInstitution[],
    onConfirmed: (institutionId: string, connectionId: string) => void
) {
    const [unconfirmed, setUnconfirmed] = useState<UnconfirmedConnection[]>([]);
    const polling = unconfirmed.filter(isPolling);

    const { data: availability } = useQuery({
        queryKey: createUseBankStatementsAvailabilityListKey(),
        queryFn: fetchBankStatementsAvailabilityList,
        enabled: polling.length > 0,
        refetchInterval: () => (unconfirmed.some(isPolling) ? UNCONFIRMED_POLL_INTERVAL_MS : false),
        refetchOnWindowFocus: false,
        retry: false,
    });

    useEffect(() => {
        if (!availability || unconfirmed.length === 0) {
            return;
        }
        const settled = new Set<string>();
        unconfirmed.forEach(({ connectionId }) => {
            const institutionId = resolveConnectedInstitutionId(availability, connectionId, institutions);
            if (institutionId === undefined) {
                return;
            }
            settled.add(connectionId);
            if (institutionId) {
                onConfirmed(institutionId, connectionId);
            }
        });
        if (settled.size > 0) {
            setUnconfirmed((prev) => prev.filter(({ connectionId }) => !settled.has(connectionId)));
        }
    }, [availability, unconfirmed, institutions, onConfirmed]);

    // Re-render when the earliest window closes, so its bank stops showing as confirming.
    const [, setExpiredAt] = useState(0);
    useEffect(() => {
        const open = unconfirmed.filter(isPolling);
        if (open.length === 0) {
            return undefined;
        }
        const closesIn = Math.min(...open.map(({ since }) => since + UNCONFIRMED_POLL_WINDOW_MS)) - Date.now();
        const timer = setTimeout(() => setExpiredAt(Date.now()), Math.max(closesIn, 0));
        return () => clearTimeout(timer);
    }, [unconfirmed]);

    const addUnconfirmed = useCallback((connectionId: string, institutionId: string) => {
        setUnconfirmed((prev) => [...prev, { connectionId, institutionId, since: Date.now() }]);
    }, []);

    // Read each render: an entry stops confirming when its window ends, not when state changes.
    const confirmingIds: ReadonlySet<string> = new Set(polling.map(({ institutionId }) => institutionId));

    return { confirmingIds, addUnconfirmed };
}
